import { useState } from "react";
import { Minus, Plus, ShoppingCart, Tag, Trash2, X } from "lucide-react";
import { ACCION, EVENTO, aPesos, formatearMXN } from "@zociety/core";
import { useCarrito } from "@/contexts/CarritoContext";
import { registrarAccion } from "@/lib/analitica";
import {
  ErrorApi,
  confirmarCompra,
  previsualizarCarrito,
  reportar,
  sesionDePago,
  type ResumenApi,
} from "@/lib/api";
import PagoTarjeta from "./PagoTarjeta";

/**
 * Qué se le dice a quien acaba de pagar.
 *
 * La versión anterior decía «tu compra quedó registrada» a todo lo que no
 * acreditara clases contables. Es cierto y no sirve: quien acaba de pagar $5,900
 * por un Unlimited de 90 días recibía la misma frase que quien compró una playera,
 * y ninguna le confirmaba que ya puede reservar.
 *
 * Los Unlimited no otorgan créditos —autorizan por cobertura—, así que ese cero
 * no es un error que corregir sino un caso distinto que nombrar.
 */
export function mensajeDeExito(creditos: number, comprado: string): string {
  if (creditos > 0) {
    return creditos === 1
      ? "Pago confirmado. Se acreditó 1 clase a tu cuenta."
      : `Pago confirmado. Se acreditaron ${creditos} clases a tu cuenta.`;
  }
  /* Sin el nombre no se puede decir nada útil, y se prefiere lo genérico a inventar. */
  return comprado
    ? `Pago confirmado. Ya tienes ${comprado} activo. Puedes reservar desde ahora.`
    : "Pago confirmado. Tu compra quedó registrada.";
}

export default function CarritoDrawer() {
  const { items, abierto, setAbierto, quitar, cambiarCantidad, subtotalCentavos, vaciar, aLineas } =
    useCarrito();

  const [codigo, setCodigo] = useState("");
  const [cuponAplicado, setCuponAplicado] = useState<ResumenApi | null>(null);
  const [validando, setValidando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);

  /** Cobro abierto en Stripe, esperando la tarjeta. Nulo el resto del tiempo. */
  const [pago, setPago] = useState<{
    secreto: string;
    secretoSesion: string | null;
    intentoId: string;
    montoCentavos: number;
  } | null>(null);

  if (!abierto) return null;

  const descuento = cuponAplicado?.descuento_centavos ?? 0;
  const total = Math.max(0, subtotalCentavos - descuento);

  const aplicarCupon = async () => {
    if (!codigo.trim()) return;
    setValidando(true);
    setError(null);
    try {
      const r = await previsualizarCarrito({ codigo: codigo.trim().toUpperCase(), lineas: aLineas() });
      setCuponAplicado(r);
      registrarAccion(ACCION.CUPON_INTENTADO, {
        resultado: "aceptado",
        /* El código del cupón sí puede viajar: identifica una campaña, no a una
           persona. Es justo lo que hace falta para saber cuál funcionó. */
        coupon: r.cupon_codigo ?? "",
        descuento: aPesos(r.descuento_centavos ?? 0),
      });
    } catch (e) {
      setCuponAplicado(null);
      /*
       * Un cupón rechazado no es una falla del sistema, es un dato de negocio.
       *
       * En la prueba real apareció `cupon_inexistente` y no había forma de saber
       * si fue un dedazo, un código caducado o una campaña que nadie comunicó.
       * Se registra el código con el que se falló, que es lo que distingue las
       * tres cosas.
       */
      registrarAccion(ACCION.CUPON_INTENTADO, {
        resultado: "rechazado",
        coupon: codigo.trim().toUpperCase().slice(0, 40),
        motivo: e instanceof ErrorApi ? e.codigo : "desconocido",
      });
      setError(e instanceof ErrorApi ? e.message : "No pudimos validar el cupón.");
    } finally {
      setValidando(false);
    }
  };

  const lineasBase = () => ({
    lineas: aLineas(),
    ...(cuponAplicado?.cupon_codigo ? { cupon_codigo: cuponAplicado.cupon_codigo } : {}),
  });

  /**
   * Cierra la compra contra el servidor.
   *
   * Es el servidor quien decide si hubo cobro: se lo pregunta a la pasarela y
   * compara el monto con el que recalculó él. Creerle al navegador permitiría
   * acreditar paquetes sin pago.
   */
  const cerrarCompra = async (intentoId: string) => {
    /* Se nombran antes de vaciar el carrito, que es de donde sale el texto. */
    const comprado = items.map((i) => i.nombre).join(", ");

    /*
     * Aquí NO se emite `purchase`, y es una decisión, no un olvido.
     *
     * La venta la sabe de verdad el servidor: si la clienta cierra la pestaña
     * justo después de pagar, un evento de navegador no sale nunca y el reporte
     * queda por debajo del dinero real. Es la misma ventana que dejaba cobros sin
     * acreditar sin que nadie se enterara.
     *
     * Emitirlo aquí «mientras tanto» sería peor que no tenerlo: cuando el
     * servidor lo mande, cada venta se contaría dos veces, y unos ingresos
     * inflados son más difíciles de detectar que unos que faltan. El catálogo lo
     * declara `origen: "servidor"` justamente para que esto no se decida dos veces.
     */
    const cierre = await confirmarCompra({ ...lineasBase(), payment_intent_id: intentoId });
    if (cierre.requiere_pago) throw new ErrorApi("pago_rechazado", "El pago no pudo completarse.");

    setExito(mensajeDeExito(cierre.creditos_acreditados, comprado));
    setPago(null);
    vaciar();
    setCuponAplicado(null);
  };

  const pagar = async () => {
    setProcesando(true);
    setError(null);

    /*
     * El escalón del embudo que importa.
     *
     * La caída entre esto y la compra confirmada **es** el abandono del carrito.
     * Se emite antes de llamar al servidor y no después: si la apertura del cobro
     * falla, esa persona intentó pagar igual y tiene que contar.
     *
     * La compra confirmada NO se emite desde aquí a propósito; ver más abajo.
     */
    registrarAccion(
      ACCION.INICIAR_PAGO,
      {
        currency: "MXN",
        value: aPesos(total),
        ...(cuponAplicado?.cupon_codigo ? { coupon: cuponAplicado.cupon_codigo } : {}),
      },
      items.slice(0, 10).map((i) => ({
        item_id: i.item_id,
        item_name: i.nombre,
        price: aPesos(i.precio_centavos),
        quantity: i.cantidad,
      }))
    );

    try {
      // El cobro va en dos tiempos: primero se abre el intento contra la
      // pasarela y luego se confirma. Así el backend nunca acredita un paquete
      // sin haber verificado el pago con el proveedor.
      const apertura = await confirmarCompra(lineasBase());

      if (!apertura.requiere_pago) {
        /* Compra sin cobro: un cupón que cubre el total, o un regalo. */
        setExito(mensajeDeExito(apertura.creditos_acreditados, items.map((i) => i.nombre).join(", ")));
        vaciar();
        setCuponAplicado(null);
        return;
      }

      /*
       * Con Stripe falta que la clienta meta su tarjeta.
       *
       * El intento queda esperando un método de pago, así que confirmarlo ahora
       * fallaría. Se abre el paso de la tarjeta y el cierre ocurre cuando Stripe
       * responde. Con la pasarela de simulación no hay tarjeta que pedir y se
       * cierra de inmediato, que es lo que mantiene las pruebas end-to-end y los
       * emuladores funcionando sin tocar una pasarela real.
       */
      if (apertura.pasarela === "stripe" && apertura.intento.secreto_cliente) {
        /*
         * El permiso para ver las tarjetas guardadas es opcional.
         *
         * Si falla, se cobra pidiendo la tarjeta como siempre: perder la comodidad
         * de reutilizar una tarjeta es mucho mejor que perder la venta.
         */
        const sesion = await sesionDePago({}).catch((e) => {
          reportar(EVENTO.SESION_PAGO_FALLIDA, e, { intento_id: apertura.intento.id });
          return { secreto_cliente: null };
        });

        setPago({
          secreto: apertura.intento.secreto_cliente,
          secretoSesion: sesion.secreto_cliente,
          intentoId: apertura.intento.id,
          montoCentavos: apertura.resumen.total_centavos,
        });
        return;
      }

      await cerrarCompra(apertura.intento.id);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : "No pudimos procesar tu compra.");
    } finally {
      setProcesando(false);
    }
  };

  /** Stripe ya cobró; solo queda que el servidor lo verifique y acredite. */
  const tarjetaConfirmada = async () => {
    if (!pago) return;
    setProcesando(true);
    setError(null);
    try {
      await cerrarCompra(pago.intentoId);
    } catch (e) {
      /*
       * Aquí ya salió el dinero de su tarjeta.
       *
       * Es el único punto de la aplicación donde un fallo deja a alguien pagado y
       * sin lo que compró. Hasta ahora la única forma de enterarnos era que ella
       * escribiera, tal como se lo pide el mensaje de abajo; quien no escribiera,
       * pagaba y no recibía.
       *
       * `conciliarCobros` lo detecta igual preguntándole a la pasarela, pero esto
       * llega en el momento y con el motivo, que es lo que hace falta para
       * resolverlo el mismo día.
       */
      reportar(EVENTO.PAGO_NO_ACREDITADO, e, {
        intento_id: pago.intentoId,
        monto_centavos: pago.montoCentavos,
      });
      setError(e instanceof ErrorApi ? e.message : "El pago se hizo pero no pudimos acreditarlo. Escríbenos.");
    } finally {
      setProcesando(false);
    }
  };

  /*
   * No se cierra en medio de un cobro.
   *
   * Con el paso de la tarjeta abierto hay un intento vivo en Stripe. Un clic
   * fuera del cajón —el gesto normal para cerrar— podría ocurrir justo entre el
   * cobro y su acreditación, y ahí sí se pierde el rastro de un pago hecho. Para
   * salir está el botón «Volver al carrito».
   */
  const cerrar = () => {
    if (pago || procesando) return;
    setAbierto(false);
    setExito(null);
    setError(null);
  };

  return (
    <div className="velo" onClick={cerrar} role="dialog" aria-modal="true" aria-label="Carrito de compra">
      <aside className="cajon" onClick={(e) => e.stopPropagation()}>
        <header className="fila entre" style={{ marginBottom: "var(--e5)" }}>
          <h3 className="fila g2">
            <ShoppingCart size={20} aria-hidden />
            Tu carrito
          </h3>
          <button className="btn btn-icono btn-fantasma" onClick={cerrar} aria-label="Cerrar carrito">
            <X size={18} />
          </button>
        </header>

        {exito ? (
          <div className="pila g4 centro" style={{ textAlign: "center", paddingBlock: "var(--e6)" }}>
            <div className="aviso aviso-exito">{exito}</div>
            <button className="btn btn-primario" onClick={cerrar}>
              Continuar
            </button>
          </div>
        ) : pago ? (
          <div className="pila g4">
            <div className="fila entre">
              <span className="suave pequeno">Total a pagar</span>
              <strong>{formatearMXN(pago.montoCentavos)}</strong>
            </div>

            {error && (
              <div className="aviso aviso-error" role="alert">
                {error}
              </div>
            )}

            <PagoTarjeta
              secretoCliente={pago.secreto}
              secretoSesion={pago.secretoSesion}
              montoCentavos={pago.montoCentavos}
              onPagado={tarjetaConfirmada}
              onCancelar={() => {
                setPago(null);
                setError(null);
              }}
            />
          </div>
        ) : items.length === 0 ? (
          <div className="pila g3 centro" style={{ textAlign: "center", paddingBlock: "var(--e7)" }}>
            <ShoppingCart size={40} className="tenue" aria-hidden />
            <p className="suave">Tu carrito está vacío.</p>
          </div>
        ) : (
          <>
            <ul className="pila g3" style={{ listStyle: "none" }}>
              {items.map((item) => (
                <li key={`${item.item_id}-${item.bday_config?.fecha ?? ""}`} className="tarjeta" style={{ padding: "var(--e4)" }}>
                  <div className="fila entre g3" style={{ alignItems: "flex-start" }}>
                    <div className="pila g1 crece">
                      <strong className="pequeno">{item.nombre}</strong>
                      <span className="diminuto tenue">
                        {formatearMXN(item.precio_centavos)} c/u
                      </span>
                      {item.bday_config && (
                        <span className="insignia insignia-acento">
                          {item.bday_config.fecha} · {item.bday_config.hora} ·{" "}
                          {item.bday_config.pago_modalidad === "50" ? "Apartado 50%" : "Pago completo"}
                        </span>
                      )}
                    </div>
                    <button
                      className="btn btn-icono btn-fantasma"
                      onClick={() => quitar(item.item_id)}
                      aria-label={`Quitar ${item.nombre}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <div className="fila entre" style={{ marginTop: "var(--e3)" }}>
                    {item.bday_config ? (
                      <span className="diminuto tenue">Evento único</span>
                    ) : (
                      <div className="fila g2">
                        <button
                          className="btn btn-icono btn-contorno btn-sm"
                          onClick={() => cambiarCantidad(item.item_id, item.cantidad - 1)}
                          aria-label="Quitar una unidad"
                        >
                          <Minus size={13} />
                        </button>
                        <span className="mono-num fuerte" style={{ minWidth: 22, textAlign: "center" }}>
                          {item.cantidad}
                        </span>
                        <button
                          className="btn btn-icono btn-contorno btn-sm"
                          onClick={() => cambiarCantidad(item.item_id, item.cantidad + 1)}
                          aria-label="Agregar una unidad"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    )}
                    <strong className="mono-num">{formatearMXN(item.precio_centavos * item.cantidad)}</strong>
                  </div>
                </li>
              ))}
            </ul>

            <div className="pila g2" style={{ marginTop: "var(--e5)" }}>
              <label className="etiqueta" htmlFor="cupon">
                Código promocional
              </label>
              <div className="fila g2">
                <input
                  id="cupon"
                  className="entrada"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/\s/g, ""))}
                  placeholder="ZOCIETY10"
                  autoComplete="off"
                />
                <button
                  className="btn btn-contorno"
                  onClick={aplicarCupon}
                  disabled={validando || !codigo.trim()}
                >
                  {validando ? <span className="girador" /> : <Tag size={15} />}
                  Aplicar
                </button>
              </div>
              {cuponAplicado && (
                <p className="diminuto" style={{ color: "var(--exito)" }}>
                  Cupón {cuponAplicado.cupon_codigo} aplicado: −{formatearMXN(descuento)}
                </p>
              )}
            </div>

            {error && (
              <div className="aviso aviso-error" style={{ marginTop: "var(--e4)" }} role="alert">
                {error}
              </div>
            )}

            <footer
              className="pila g3"
              style={{ marginTop: "var(--e5)", paddingTop: "var(--e4)", borderTop: "1px solid var(--borde)" }}
            >
              <div className="fila entre pequeno suave">
                <span>Subtotal</span>
                <span className="mono-num">{formatearMXN(subtotalCentavos)}</span>
              </div>
              {descuento > 0 && (
                <div className="fila entre pequeno" style={{ color: "var(--exito)" }}>
                  <span>Descuento</span>
                  <span className="mono-num">−{formatearMXN(descuento)}</span>
                </div>
              )}
              <div className="fila entre">
                <strong>Total</strong>
                <strong className="mono-num" style={{ fontSize: "var(--texto-lg)" }}>
                  {formatearMXN(total)}
                </strong>
              </div>

              <button className="btn btn-primario btn-bloque btn-lg" onClick={pagar} disabled={procesando}>
                {procesando ? <span className="girador" /> : null}
                {procesando ? "Procesando…" : "Pagar"}
              </button>
              <p className="diminuto tenue" style={{ textAlign: "center" }}>
                Pago seguro. Tus datos de tarjeta nunca pasan por nuestros servidores.
              </p>
            </footer>
          </>
        )}
      </aside>

      <style>{`
        .velo:has(.cajon) { place-items: stretch; justify-content: flex-end; padding: 0; }
        .cajon {
          width: min(440px, 100vw);
          height: 100dvh;
          overflow-y: auto;
          padding: var(--e5);
          background: var(--fondo-elevado);
          border-left: 1px solid var(--borde-fuerte);
          box-shadow: var(--sombra-lg);
          animation: entrar-derecha var(--dur-media) var(--curva-suave);
        }
        @keyframes entrar-derecha { from { transform: translateX(100%); } }
      `}</style>
    </div>
  );
}
